import React, { useState } from 'react';
import { 
  FaFileAlt, 
  FaPassport, 
  FaMoneyCheckAlt, 
  FaHome, 
  FaPlane,
  FaUniversity,
  FaBriefcase,
  FaUsers,
  FaDownload,
  FaPrint,
  FaQuestionCircle,
  FaGlobeAmericas,
  FaFlag,
  FaCheckCircle,
  FaTimesCircle,
  FaCamera,
  FaStethoscope,
  FaBalanceScale,
  FaCertificate
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const RequirementsPage = () => {
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedVisaType, setSelectedVisaType] = useState('tourist');
  const [activeSection, setActiveSection] = useState('documents');

  const countries = [
    { code: 'us', name: 'United States', flag: '🇺🇸' },
    { code: 'uk', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'ca', name: 'Canada', flag: '🇨🇦' },
    { code: 'au', name: 'Australia', flag: '🇦🇺' },
    { code: 'de', name: 'Germany', flag: '🇩🇪' },
    { code: 'fr', name: 'France', flag: '🇫🇷' },
    { code: 'jp', name: 'Japan', flag: '🇯🇵' },
    { code: 'sg', name: 'Singapore', flag: '🇸🇬' },
  ];

  const visaTypes = [
    { id: 'tourist', name: 'Tourist Visa', icon: <FaPlane /> },
    { id: 'student', name: 'Student Visa', icon: <FaUniversity /> },
    { id: 'business', name: 'Business Visa', icon: <FaBriefcase /> },
    { id: 'work', name: 'Work Visa', icon: <FaBriefcase /> },
    { id: 'family', name: 'Family Visa', icon: <FaUsers /> },
  ];

  const documentSections = {
    documents: {
      title: 'Required Documents',
      icon: <FaFileAlt />,
      content: {
        tourist: [
          { name: 'Valid Passport', required: true, details: 'Minimum 6 months validity, 2 blank pages' },
          { name: 'Visa Application Form', required: true, details: 'Completed and signed' },
          { name: 'Passport Photos', required: true, details: '2 recent color photos, 35x45mm' },
          { name: 'Flight Itinerary', required: true, details: 'Round-trip confirmed tickets' },
          { name: 'Hotel Reservations', required: true, details: 'Confirmed accommodation proof' },
          { name: 'Travel Insurance', required: true, details: 'Minimum €30,000 coverage' },
          { name: 'Bank Statements', required: true, details: 'Last 3-6 months, sufficient funds' },
          { name: 'Employment Letter', required: true, details: 'From current employer' },
          { name: 'Invitation Letter', required: false, details: 'If visiting friends/family' },
          { name: 'Previous Visas', required: false, details: 'Copies of previous visas' },
        ],
        student: [
          { name: 'University Admission Letter', required: true, details: 'Official acceptance letter' },
          { name: 'Valid Passport', required: true, details: 'Minimum 6 months validity' },
          { name: 'Academic Transcripts', required: true, details: 'Official records from previous institutions' },
          { name: 'Language Proficiency', required: true, details: 'IELTS/TOEFL test scores' },
          { name: 'Financial Proof', required: true, details: 'Bank statements showing sufficient funds' },
          { name: 'Sponsorship Letter', required: false, details: 'If sponsored by family/organization' },
          { name: 'Medical Insurance', required: true, details: 'Health coverage for study period' },
          { name: 'CV/Resume', required: true, details: 'Detailed academic and professional background' },
          { name: 'Statement of Purpose', required: true, details: 'Academic goals and plans' },
          { name: 'Accommodation Proof', required: true, details: 'Confirmed housing arrangements' },
        ],
        business: [
          { name: 'Business Invitation Letter', required: true, details: 'From host company in destination' },
          { name: 'Valid Passport', required: true, details: 'Minimum 6 months validity' },
          { name: 'Company Registration', required: true, details: 'Proof of business registration' },
          { name: 'Bank Statements', required: true, details: 'Company financial records' },
          { name: 'Employment Letter', required: true, details: 'From current employer' },
          { name: 'Conference Details', required: false, details: 'If attending conference/meeting' },
          { name: 'Business Cards', required: false, details: 'Current business cards' },
          { name: 'Tax Returns', required: true, details: 'Last 2-3 years tax documents' },
          { name: 'Hotel Reservations', required: true, details: 'Confirmed accommodation' },
          { name: 'Flight Itinerary', required: true, details: 'Round-trip tickets' },
        ]
      }
    },
    eligibility: {
      title: 'Eligibility Criteria',
      icon: <FaCheckCircle />,
      content: {
        tourist: [
          { criteria: 'Financial Requirements', description: 'Minimum bank balance: $3,000 - $5,000', mandatory: true },
          { criteria: 'Travel History', description: 'Previous international travel preferred', mandatory: false },
          { criteria: 'Employment Status', description: 'Stable job or regular income source', mandatory: true },
          { criteria: 'Return Intent', description: 'Strong ties to home country', mandatory: true },
          { criteria: 'Health Requirements', description: 'No contagious diseases', mandatory: true },
          { criteria: 'Criminal Record', description: 'No serious criminal history', mandatory: true },
          { criteria: 'Insurance Coverage', description: 'Valid travel medical insurance', mandatory: true },
          { criteria: 'Accommodation Proof', description: 'Confirmed hotel/host arrangements', mandatory: true },
        ],
        student: [
          { criteria: 'Academic Qualifications', description: 'Meet minimum GPA requirements', mandatory: true },
          { criteria: 'Language Proficiency', description: 'IELTS 6.0+ or equivalent', mandatory: true },
          { criteria: 'Financial Capability', description: 'Proof of tuition + living expenses', mandatory: true },
          { criteria: 'Course Acceptance', description: 'Full-time course at recognized institution', mandatory: true },
          { criteria: 'Health Insurance', description: 'Comprehensive medical coverage', mandatory: true },
          { criteria: 'Genuine Student Test', description: 'Intent to study and return', mandatory: true },
          { criteria: 'Age Requirements', description: 'Typically 18+ years', mandatory: true },
          { criteria: 'Background Check', description: 'Clean criminal record', mandatory: true },
        ]
      }
    },
    process: {
      title: 'Application Process',
      icon: <FaCertificate />,
      content: {
        tourist: [
          { step: 1, title: 'Document Preparation', time: '3-7 days', description: 'Gather all required documents' },
          { step: 2, title: 'Form Completion', time: '1-2 days', description: 'Fill out visa application forms' },
          { step: 3, title: 'Appointment Booking', time: '1-7 days', description: 'Schedule biometrics appointment' },
          { step: 4, title: 'Submission', time: '1 day', description: 'Submit documents at visa center' },
          { step: 5, title: 'Processing', time: '5-15 days', description: 'Embassy/consulate review' },
          { step: 6, title: 'Decision', time: '1-2 days', description: 'Receive visa decision' },
          { step: 7, title: 'Passport Collection', time: '1-3 days', description: 'Collect passport with visa' },
        ],
        student: [
          { step: 1, title: 'University Admission', time: '2-8 weeks', description: 'Get acceptance letter from institution' },
          { step: 2, title: 'Financial Preparation', time: '1-2 weeks', description: 'Arrange funds and financial proof' },
          { step: 3, title: 'Document Collection', time: '1-3 weeks', description: 'Gather academic and personal documents' },
          { step: 4, title: 'Visa Application', time: '1-2 days', description: 'Complete online application' },
          { step: 5, title: 'Biometrics', time: '1-3 days', description: 'Provide fingerprints and photo' },
          { step: 6, title: 'Interview', time: '1-2 weeks', description: 'Attend visa interview if required' },
          { step: 7, title: 'Processing', time: '4-8 weeks', description: 'Embassy review and decision' },
        ]
      }
    },
    tips: {
      title: 'Important Tips',
      icon: <FaQuestionCircle />,
      content: {
        tourist: [
          { tip: 'Apply Early', importance: 'high', description: 'Apply at least 3 months before travel' },
          { tip: 'Accurate Information', importance: 'high', description: 'Ensure all details match your documents' },
          { tip: 'Sufficient Funds', importance: 'high', description: 'Maintain consistent bank balance' },
          { tip: 'Travel Insurance', importance: 'high', description: 'Purchase before applying' },
          { tip: 'Clear Itinerary', importance: 'medium', description: 'Have detailed travel plans' },
          { tip: 'Strong Home Ties', importance: 'high', description: 'Show reasons to return home' },
          { tip: 'Professional Photos', importance: 'medium', description: 'Use visa-specific photo service' },
          { tip: 'Document Copies', importance: 'medium', description: 'Keep copies of all submissions' },
        ],
        student: [
          { tip: 'Early Application', importance: 'high', description: 'Apply 3-4 months before semester starts' },
          { tip: 'Financial Planning', importance: 'high', description: 'Show funds for entire study period' },
          { tip: 'Genuine Intent', importance: 'high', description: 'Clearly explain study goals' },
          { tip: 'Health Check', importance: 'high', description: 'Complete medical exam if required' },
          { tip: 'Backup Documents', importance: 'medium', description: 'Prepare additional supporting evidence' },
          { tip: 'Interview Preparation', importance: 'high', description: 'Practice common interview questions' },
          { tip: 'Course Understanding', importance: 'high', description: 'Know details about your program' },
          { tip: 'Post-Study Plans', importance: 'medium', description: 'Have clear career goals' },
        ]
      }
    }
  };

  const countrySpecificRequirements = {
    us: {
      tourist: {
        special: 'ESTA for Visa Waiver Program eligible countries',
        interview: 'Usually required for first-time applicants',
        biometrics: 'Required at application center',
        fees: '$185 application fee',
        processing: '3-5 weeks standard processing'
      },
      student: {
        special: 'SEVIS fee payment required',
        interview: 'Mandatory for all applicants',
        biometrics: 'Required',
        fees: '$185 visa fee + $350 SEVIS fee',
        processing: '4-8 weeks'
      }
    },
    uk: {
      tourist: {
        special: 'Visit visa for tourism purposes',
        interview: 'Not usually required',
        biometrics: 'Required at visa center',
        fees: '£115 standard visit visa',
        processing: '3 weeks standard'
      },
      student: {
        special: 'CAS number from institution required',
        interview: 'Credibility interview possible',
        biometrics: 'Required',
        fees: '£363 student visa fee',
        processing: '3 weeks inside UK, 3 months outside'
      }
    }
  };

  const getCurrentContent = () => {
    const section = documentSections[activeSection];
    return section.content[selectedVisaType] || [];
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-300 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="relative container-padding py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Visa Requirements
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-primary-100 mb-8"
            >
              Comprehensive guide to document requirements, eligibility criteria, and application processes
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3"
            >
              <FaCheckCircle className="mr-3" />
              <span className="font-medium">Updated for 2024 • Official Guidelines</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Selection Controls */}
      <section className="py-8 bg-white shadow-lg">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Country Selector */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Select Destination Country
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCountry('all')}
                    className={`flex items-center px-4 py-2 rounded-lg transition ${
                      selectedCountry === 'all'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FaGlobeAmericas className="mr-2" />
                    All Countries
                  </button>
                  {countries.map(country => (
                    <button
                      key={country.code}
                      onClick={() => setSelectedCountry(country.code)}
                      className={`flex items-center px-4 py-2 rounded-lg transition ${
                        selectedCountry === country.code
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-2">{country.flag}</span>
                      {country.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Visa Type Selector */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Select Visa Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {visaTypes.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedVisaType(type.id)}
                      className={`flex items-center px-4 py-2 rounded-lg transition ${
                        selectedVisaType === type.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-2">{type.icon}</span>
                      {type.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-gray-50 border-b">
        <div className="container-padding py-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-700 mb-4 md:mb-0">
              <span className="font-bold">
                {selectedCountry === 'all' ? 'All Countries' : countries.find(c => c.code === selectedCountry)?.name}
              </span>
              <span className="mx-2">•</span>
              <span className="font-bold">
                {visaTypes.find(v => v.id === selectedVisaType)?.name}
              </span>
              <span className="mx-2">•</span>
              <span>{documentSections[activeSection]?.title}</span>
            </div>
            
            <div className="flex gap-3">
              <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                <FaDownload className="mr-2" />
                Download Checklist
              </button>
              <button 
                onClick={handlePrint}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <FaPrint className="mr-2" />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            {/* Section Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {Object.entries(documentSections).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`flex items-center px-6 py-3 rounded-xl transition ${
                    activeSection === key
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <span className="mr-3">{section.icon}</span>
                  {section.title}
                </button>
              ))}
            </div>

            {/* Content Display */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSection + selectedVisaType}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  {/* Documents Section */}
                  {activeSection === 'documents' && (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-2xl font-bold text-gray-900">
                          Required Documents for {visaTypes.find(v => v.id === selectedVisaType)?.name}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {getCurrentContent().length} items
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {getCurrentContent().map((doc, index) => (
                          <motion.div
                            key={doc.name}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-start p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                          >
                            <div className="mr-4">
                              {doc.required ? (
                                <FaCheckCircle className="text-green-500 text-xl mt-1" />
                              ) : (
                                <FaTimesCircle className="text-gray-400 text-xl mt-1" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-bold text-gray-900">{doc.name}</h4>
                                <span className={`px-3 py-1 rounded-full text-sm ${
                                  doc.required 
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {doc.required ? 'Required' : 'Optional'}
                                </span>
                              </div>
                              <p className="text-gray-600 mt-2">{doc.details}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Eligibility Section */}
                  {activeSection === 'eligibility' && (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">
                        Eligibility Criteria
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        {getCurrentContent().map((criteria, index) => (
                          <motion.div
                            key={criteria.criteria}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-bold text-gray-900">{criteria.criteria}</h4>
                              <span className={`px-2 py-1 text-xs rounded ${
                                criteria.mandatory
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {criteria.mandatory ? 'Mandatory' : 'Preferred'}
                              </span>
                            </div>
                            <p className="text-gray-600">{criteria.description}</p>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Process Section */}
                  {activeSection === 'process' && (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">
                        Application Process Timeline
                      </h3>
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="hidden md:block absolute left-0 top-8 h-1 w-full bg-gray-200"></div>
                        
                        <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-7">
                          {getCurrentContent().map((step, index) => (
                            <motion.div
                              key={step.step}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="relative text-center"
                            >
                              <div className="relative z-10">
                                <div className="w-16 h-16 mx-auto mb-4 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                                  {step.step}
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm">
                                  <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                                  <div className="text-sm text-gray-500 mb-2">{step.time}</div>
                                  <p className="text-sm text-gray-600">{step.description}</p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Tips Section */}
                  {activeSection === 'tips' && (
                    <>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">
                        Important Tips & Recommendations
                      </h3>
                      <div className="space-y-4">
                        {getCurrentContent().map((tip, index) => (
                          <motion.div
                            key={tip.tip}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl"
                          >
                            <div className="mr-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                tip.importance === 'high' 
                                  ? 'bg-red-100 text-red-600'
                                  : tip.importance === 'medium'
                                  ? 'bg-yellow-100 text-yellow-600'
                                  : 'bg-green-100 text-green-600'
                              }`}>
                                <FaQuestionCircle />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-bold text-gray-900">{tip.tip}</h4>
                                <span className={`px-3 py-1 rounded-full text-sm ${
                                  tip.importance === 'high'
                                    ? 'bg-red-100 text-red-800'
                                    : tip.importance === 'medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {tip.importance === 'high' ? 'Critical' : 
                                   tip.importance === 'medium' ? 'Important' : 'Recommended'}
                                </span>
                              </div>
                              <p className="text-gray-700">{tip.description}</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Country Specific Information */}
      {selectedCountry !== 'all' && countrySpecificRequirements[selectedCountry] && 
       countrySpecificRequirements[selectedCountry][selectedVisaType] && (
        <section className="py-12 bg-white border-t">
          <div className="container-padding">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FaFlag className="mr-3" />
                {countries.find(c => c.code === selectedCountry)?.name} Specific Requirements
              </h3>
              
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl p-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {Object.entries(countrySpecificRequirements[selectedCountry][selectedVisaType]).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-sm text-gray-500 mb-2">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </div>
                      <div className="font-bold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Reference */}
      <section className="py-20 bg-gray-50">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Quick Reference Guide
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential information for all visa applicants
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaCamera className="text-3xl" />,
                title: 'Photo Requirements',
                items: [
                  '35x45mm size',
                  'White background',
                  'Recent (last 6 months)',
                  'Neutral expression'
                ]
              },
              {
                icon: <FaStethoscope className="text-3xl" />,
                title: 'Medical Requirements',
                items: [
                  'No contagious diseases',
                  'Health insurance',
                  'Medical exam if required',
                  'Vaccination records'
                ]
              },
              {
                icon: <FaBalanceScale className="text-3xl" />,
                title: 'Financial Proof',
                items: [
                  '3-6 months statements',
                  'Minimum balance',
                  'Income proof',
                  'Tax returns'
                ]
              },
              {
                icon: <FaCertificate className="text-3xl" />,
                title: 'Important Notes',
                items: [
                  'Apply early',
                  'Be truthful',
                  'Keep copies',
                  'Track application'
                ]
              }
            ].map((reference, index) => (
              <motion.div
                key={reference.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg p-8"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                  {reference.icon}
                </div>
                <h3 className="text-xl font-bold text-center text-gray-900 mb-4">
                  {reference.title}
                </h3>
                <ul className="space-y-2">
                  {reference.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <FaCheckCircle className="mr-3 text-green-500 text-sm" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-primary-700 text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Need Help With Your Documents?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
              Our experts can review your documents and ensure everything meets the requirements
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary bg-white text-primary-700 hover:bg-primary-100 px-8 py-4 text-lg"
              >
                Get Document Review
              </a>
              <a
                href="/contact"
                className="btn-secondary border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Book Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RequirementsPage;